/**
 * ColorNavigation Page
 * ====================
 * PRIMARY ENTRY POINT
 * Visual color selector - NO dropdowns, NO text-heavy UI
 * Visual-first, premium dark theme
 * 
 * Route: /experience/colors
 * 
 * Groups come from Blueprint: features.color_navigation.groups
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlueprint } from '../context/BlueprintContext';
import Header from '../components/Header';
import { ArrowRight } from 'lucide-react';

// Color groups with visual representation
const DEFAULT_COLOR_GROUPS = [
  {
    key: 'warm_neutrals',
    title: 'Warm Neutrals',
    description: 'Beige, Cream, Gold tones',
    colors: ['beige', 'cream', 'gold'],
    gradientFrom: '#D6C1A5',
    gradientTo: '#F5F0E6',
    filterValue: ['beige', 'cream', 'gold']
  },
  {
    key: 'light_minimal',
    title: 'Light Minimal',
    description: 'Pure whites and soft greys',
    colors: ['white', 'light grey'],
    gradientFrom: '#FFFFFF',
    gradientTo: '#E8E8E8',
    filterValue: ['white']
  },
  {
    key: 'dark_stones',
    title: 'Dark Stones',
    description: 'Deep blacks and charcoals',
    colors: ['black', 'dark grey'],
    gradientFrom: '#2A2A2A',
    gradientTo: '#0A0A09',
    filterValue: ['black', 'grey']
  },
  {
    key: 'high_contrast',
    title: 'High Contrast',
    description: 'Bold veining and dramatic patterns',
    colors: ['mixed'],
    gradientFrom: '#FFFFFF',
    gradientTo: '#1A1A1A',
    filterValue: ['white', 'black'],
    isContrast: true
  }
];

// Color block component
const ColorBlock = ({ group, onClick }) => {
  const { title, description, gradientFrom, gradientTo, isContrast } = group;
  
  return (
    <button
      onClick={onClick}
      className="group relative w-full aspect-square overflow-hidden transition-all hover:scale-[1.02]"
      data-testid={`color-block-${group.key}`}
    >
      {/* Background gradient */}
      {isContrast ? (
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientFrom} 50%, ${gradientTo} 50%, ${gradientTo} 100%)`
        }} />
      ) : (
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`
        }} />
      )}
      
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-[#0A0A09]/0 group-hover:bg-[#0A0A09]/60 transition-all duration-500" />
      
      {/* Content - visible on hover */}
      <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
        <h3 className="text-xl font-light text-white mb-2">{title}</h3>
        <p className="text-xs text-white/60 mb-4">{description}</p>
        <div className="flex items-center gap-2 text-[#D6C1A5]">
          <span className="text-xs font-medium tracking-wide">Esplora</span>
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
      
      {/* Title always visible at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0A0A09]/80 to-transparent">
        <h3 className="text-sm font-medium text-white">{title}</h3>
      </div>
    </button>
  );
};

// Large color strip component (for featured groups)
const ColorStrip = ({ group, onClick }) => {
  const { title, description, gradientFrom, gradientTo, colors, isContrast } = group;
  
  return (
    <button
      onClick={onClick}
      className="group relative w-full h-48 overflow-hidden transition-all hover:scale-[1.01]"
      data-testid={`color-strip-${group.key}`}
    >
      {/* Background */}
      {isContrast ? (
        <div className="absolute inset-0" style={{
          background: `linear-gradient(90deg, ${gradientFrom} 0%, ${gradientFrom} 50%, ${gradientTo} 50%, ${gradientTo} 100%)`
        }} />
      ) : (
        <div className="absolute inset-0" style={{
          background: `linear-gradient(90deg, ${gradientFrom} 0%, ${gradientTo} 100%)`
        }} />
      )}
      
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-[#0A0A09]/0 group-hover:bg-[#0A0A09]/40 transition-all duration-500" />
      
      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-between px-12">
        <div>
          <h3 className="text-3xl font-light text-white mb-2 drop-shadow-lg">{title}</h3>
          <p className="text-sm text-white/70 drop-shadow-md">{description}</p>
        </div>
        
        <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-sm font-medium text-white">Esplora materiali</span>
          <div className="w-10 h-10 rounded-full bg-[#D6C1A5] flex items-center justify-center group-hover:scale-110 transition-transform">
            <ArrowRight size={18} className="text-[#0A0A09]" />
          </div>
        </div>
      </div>
    </button>
  );
};

const ColorNavigation = () => {
  const navigate = useNavigate();
  const { content, getFeature, getRoute } = useBlueprint();
  
  // Get color groups from config or use defaults
  const colorConfig = getFeature('color_navigation', {});
  const colorGroups = colorConfig.groups || DEFAULT_COLOR_GROUPS;
  
  // Get content
  const pageContent = content?.color_navigation || {};
  
  // Handle color selection
  const handleColorSelect = (group) => {
    const explorerRoute = getRoute('explorer', '/materials');
    const filterParam = group.filterValue.join(',');
    navigate(`${explorerRoute}?color_family=${filterParam}`);
  };
  
  return (
    <div className="min-h-screen bg-[#0A0A09]">
      <Header />
      
      <main className="pt-[68px]">
        {/* Hero Section */}
        <section className="py-20 px-8">
          <div className="max-w-[1400px] mx-auto">
            <span className="text-[10px] font-bold tracking-[3px] uppercase text-[#D6C1A5] block mb-4">
              COLOR NAVIGATION
            </span>
            <h1 className="font-[var(--ff-h)] text-[clamp(36px,5vw,64px)] font-light text-white mb-4">
              {pageContent.title || 'Inizia dal Colore'}
            </h1>
            <p className="text-lg text-white/50 max-w-2xl">
              {pageContent.description || 'Seleziona una famiglia cromatica per esplorare i materiali. Un modo visivo e intuitivo per trovare ciò che cerchi.'}
            </p>
          </div>
        </section>
        
        {/* Featured Color Strip */}
        <section className="px-8 pb-8">
          <div className="max-w-[1400px] mx-auto">
            <ColorStrip 
              group={colorGroups[0]} 
              onClick={() => handleColorSelect(colorGroups[0])} 
            />
          </div>
        </section>
        
        {/* Color Grid */}
        <section className="px-8 pb-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {colorGroups.slice(1).map((group) => (
                <ColorBlock
                  key={group.key}
                  group={group}
                  onClick={() => handleColorSelect(group)}
                />
              ))}
            </div>
          </div>
        </section>
        
        {/* Hint Section */}
        <section className="border-t border-white/5 py-12 px-8">
          <div className="max-w-[1400px] mx-auto text-center">
            <p className="text-sm text-white/30">
              {pageContent.hint || 'Ogni selezione ti porta direttamente ai materiali filtrati. Nessun menu, nessuna confusione.'}
            </p>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-white/5 py-8 bg-[#0A0A09]">
        <div className="max-w-[1400px] mx-auto px-8 text-center">
          <p className="text-xs text-white/30">
            © 2026 SlabReality. THE REAL SLAB. IN YOUR SPACE.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ColorNavigation;
